import { useEffect, useState } from "react";
import api from "@/lib/api";
import { NUM, TRY, fmtDateTime, unitLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PencilRuler, ArrowRight, ArrowLeft, History, Loader2 } from "lucide-react";
import { EditableCell } from "./EditableCell";
import { SplitSlider } from "./SplitSlider";
import { Thumb } from "./PortalTable";
import { PurchaseHistoryPanel } from "@/components/stock/purchase/PurchaseHistoryPanel";

const INFO = [["code", "Kod"], ["barcode", "Barkod"], ["brand", "Marka"], ["series", "Seri"], ["category", "Kategori"], ["sub_category", "Alt grup"], ["discount_group", "İskonto grubu"], ["unit", "Birim"], ["box_qty", "Koli adedi", "number"], ["critical_level", "Kritik seviye", "number"]];
const PRICE = [["list_price", "Liste fiyatı", "number"], ["currency", "Döviz", "cur"], ["kdv_rate", "KDV (%)", "number"], ["price_kdv_included", "Liste fiyatı KDV dahil", "bool"], ["price_general", "Genel satış (KDV dahil)", "number"], ["price_elec_vat", "Elektrikçi faturalı", "number"], ["price_elec_novat", "Elektrikçi KDV'siz", "number"], ["cost_manual", "Manuel maliyet", "number"]];
const MOVE_TR = { giris: "Giriş", cikis: "Çıkış", sayim: "Sayım", transfer: "Aktarma", duzeltme: "Düzeltme", hizli_satis: "Hızlı Satış", hizli_satis_iptal: "Satış İptali", olusturma: "Yeni Ürün" };

function Field({ p, f, label, kind, onSave }) {
  const v = p[f];
  const props = { testid: `drawer-edit-${f}`, onSave: (x) => onSave(f, kind === "bool" ? x === "true" : x) };
  if (kind === "cur") return <EditableCell value={v || "TL"} options={["TL", "USD", "EUR"]} {...props} />;
  if (kind === "bool") return <EditableCell value={String(!!v)} display={v ? "Evet" : "Hayır"} options={[{ value: "false", label: "Hayır" }, { value: "true", label: "Evet" }]} {...props} />;
  if (kind === "number") return <EditableCell type="number" value={v ?? ""} display={v == null || v === "" ? undefined : NUM(v)} {...props} />;
  return <EditableCell value={v} display={f === "unit" ? unitLabel(v) : undefined} {...props} />;
}

function Grid({ p, rows, onSave }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {rows.map(([f, l, k]) => (
        <div key={f} className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1.5">{l}</div>
          <div className="text-[13px] text-slate-800"><Field p={p} f={f} label={l} kind={k} onSave={onSave} /></div>
        </div>
      ))}
    </div>
  );
}

function Moves({ pid, stamp }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); api.get(`/stock/movements?product_id=${pid}&limit=15`).then((r) => setRows(r.data || [])).catch(() => setRows([])); }, [pid, stamp]);
  if (rows === null) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  if (!rows.length) return <p className="text-xs text-slate-400">Hareket yok</p>;
  return (
    <ol className="space-y-1.5" data-testid="drawer-moves">
      {rows.map((m) => (
        <li key={m.id} className="flex items-start gap-2 text-xs border-l-2 border-slate-200 pl-2.5">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-700">{MOVE_TR[m.type] || m.type}{m.location === "magaza" || m.type === "transfer" ? <span className="text-teal-700"> · {m.type === "transfer" ? (m.location === "magaza" ? "Depo → Mağaza" : "Mağaza → Depo") : "Mağaza"}</span> : null}{m.undone && <span className="text-red-500"> · geri alındı</span>}</div>
            <div className="text-slate-500 truncate">{m.note}</div>
            <div className="text-[10px] text-slate-400">{fmtDateTime(m.created_at)} · {m.user_name}</div>
          </div>
          <span className="tnum text-slate-600 shrink-0">{NUM(m.old_qty)} → <b>{NUM(m.new_qty)}</b></span>
        </li>
      ))}
    </ol>
  );
}

/** Sağdan açılan ürün ayrıntı paneli: stok dağılımı, aktarma, tüm alanlar (anında kayıt), son hareketler. */
export function ProductDrawer({ p, onClose, h, onOpenEdit }) {
  const [qty, setQty] = useState("");
  const [stamp, setStamp] = useState(0);
  useEffect(() => { setQty(""); }, [p?.id]);
  if (!p) return null;
  const save = (f, v) => h.saveField(p, f, v);
  const move = async (to, q) => { await h.move(p, to, q); setStamp((s) => s + 1); setQty(""); };
  const loc = async (l, v) => { await h.saveLoc(p, l, v); setStamp((s) => s + 1); };
  const big = (lbl, v, cls, l) => (
    <div className={cn("rounded-xl border p-2.5", cls)}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{lbl}</div>
      {l ? <EditableCell type="number" value={v} display={NUM(v)} onSave={(x) => loc(l, x)} testid={`drawer-edit-qty-${l}`} className="font-head font-black text-xl tnum px-0" />
        : <div className="font-head font-black text-xl tnum py-1">{NUM(v)}</div>}
    </div>
  );
  return (
    <Sheet open={!!p} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="sheet-product-detail">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-start gap-3 pr-10">
          <Thumb p={p} size="h-14 w-14" />
          <div className="min-w-0 flex-1">
            <SheetTitle className="sr-only">{p.name}</SheetTitle>
            <SheetDescription className="sr-only">Ürün ayrıntıları</SheetDescription>
            <EditableCell value={p.name} onSave={(v) => save("name", v)} testid="drawer-edit-name" className="font-head font-bold text-[15px] text-[#0F2745] leading-snug" />
            <div className="text-[11px] text-slate-500 px-1.5">{[p.brand, p.code].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto space-y-5 flex-1">
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Stok Dağılımı</div>
            <div className="grid grid-cols-3 gap-2">
              {big("Depo", p.qty_depo, "bg-slate-50 border-slate-200 text-slate-700", "depo")}
              {big("Mağaza", p.qty_magaza, "bg-teal-50 border-teal-200 text-teal-800", "magaza")}
              {big(`Toplam · ${unitLabel(p.unit)}`, p.quantity, "bg-[#0F2745] border-[#0F2745] text-white")}
            </div>
            <div className="mt-3"><SplitSlider depo={p.qty_depo} magaza={p.qty_magaza} unit={unitLabel(p.unit)} onCommit={({ to, qty: q }) => move(to, q)} testid="drawer-slider" /></div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Input type="number" inputMode="decimal" placeholder="Adet" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 w-24" data-testid="drawer-move-qty" />
              <Button size="sm" variant="outline" className="h-9 border-teal-300 text-teal-700" disabled={!(Number(qty) > 0)} onClick={() => move("magaza", Number(qty)).catch(() => {})} data-testid="drawer-move-magaza">Depo<ArrowRight className="h-3.5 w-3.5 mx-1" />Mağaza</Button>
              <Button size="sm" variant="outline" className="h-9" disabled={!(Number(qty) > 0)} onClick={() => move("depo", Number(qty)).catch(() => {})} data-testid="drawer-move-depo">Mağaza<ArrowLeft className="h-3.5 w-3.5 mx-1" />Depo</Button>
            </div>
          </section>
          <section><div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Ürün Bilgileri</div><Grid p={p} rows={INFO} onSave={save} />
            <div className="mt-1.5"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1.5">Açıklama</div><EditableCell value={p.description} onSave={(v) => save("description", v)} testid="drawer-edit-description" className="text-[13px]" placeholder="açıklama ekle" /></div>
          </section>
          <section>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Fiyat & Maliyet</div>
            <Grid p={p} rows={PRICE} onSave={save} />
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]" data-testid="drawer-price-summary">
              {[["TL liste", TRY(p.price)], ["Maliyet", `${TRY(p.cost)}${p.cost_source && p.cost_source !== "auto" ? ` · ${p.cost_source === "manual" ? "manuel" : "fatura"}` : ""}`], ["Satış", TRY(p.sale_price)], ["Stok değeri", TRY(p.value)]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5"><div className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">{l}</div><div className="tnum font-semibold text-slate-800">{v}</div></div>
              ))}
            </div>
          </section>
          <section><PurchaseHistoryPanel productId={p.id} compact /></section>
          <section><div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Son Hareketler</div><Moves pid={p.id} stamp={stamp} /></section>
        </div>
        <div className="p-3 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Kapat</Button>
          <Button onClick={() => onOpenEdit(p)} data-testid="btn-drawer-full-edit"><PencilRuler className="h-4 w-4 mr-2" />Tam Düzenleme</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
